import './ui/styles.css';
import { cityRegistry } from './core/registry';
import { InvisibleCitiesEngine, type EngineTelemetry } from './core/engine';
import { isidoraCityDefinition } from './cities/isidora/index';
import { IsidoraSoundscape } from './audio/soundscape';
import { IsidoraHUD } from './ui/hud';
import type { Locale, MotifId } from './core/types';

// 1. Register Isidora in the extensible Invisible Cities Registry
cityRegistry.register(isidoraCityDefinition);

// 2. Parse URL Query Parameters for programmable inspection & testing
const params = new URLSearchParams(window.location.search);
const requestedCityId = params.get('city') ?? 'isidora';
const initialMode = params.get('mode') === 'specimen' ? 'specimen' : 'city';
const initialSpecimen = params.get('specimen') ?? 'seashell';
const initialPoi = (params.get('poi') as MotifId | null) ?? 'overview';
const initialTimeParam = params.get('time');
const initialWireframe = params.get('wireframe') === '1';
const initialInspectorOpen =
  params.get('inspector') === '1' || initialMode === 'specimen';
const initialLocale: Locale = params.get('lang') === 'en' ? 'en' : 'zh';

const cityDef = cityRegistry.get(requestedCityId) ?? cityRegistry.getDefault();
const canvasContainer = document.getElementById('canvas-container');
const uiRoot = document.getElementById('ui-root');

if (!canvasContainer || !uiRoot) {
  throw new Error('Missing #canvas-container or #ui-root mount elements.');
}

const soundscape = new IsidoraSoundscape();

let hud: IsidoraHUD | null = null;

const engine = new InvisibleCitiesEngine(canvasContainer, cityDef, {
  onHoverInteractive: (item, screenPos) => {
    hud?.updateHoverTooltip(item, screenPos);
  },
  onActivateInteractive: (item) => {
    if (!soundscape.isEnabled()) {
      soundscape.enable();
    }
    if (item.kind === 'violin-string') {
      soundscape.playViolinString(item.frequency ?? 440);
    } else if (item.kind === 'seashell') {
      soundscape.playSeashellResonance(item.frequency ?? 293.66);
    } else if (item.kind === 'memory-wall') {
      engine.selectPOI('wall', true);
      soundscape.playViolinString(220.0);
    } else if (item.kind === 'crossroads-arch') {
      engine.selectPOI('crossroads', true);
      soundscape.playSeashellResonance(349.23);
    } else {
      soundscape.playMotifCue(item.motif, item.frequency);
    }
  },
  onPoiChanged: (poi) => {
    hud?.syncActivePOI(poi);
  },
  onTelemetryUpdate: (telemetry) => {
    hud?.updateTelemetry(telemetry);
  },
});

const hudInstance = new IsidoraHUD(uiRoot, engine, soundscape, {
  initialInspectorOpen,
  initialLocale,
});
hud = hudInstance;

// Apply initial URL state
if (initialPoi && initialMode === 'city') {
  engine.selectPOI(initialPoi, initialTimeParam === null);
}
if (initialTimeParam !== null) {
  const parsedTime = parseFloat(initialTimeParam);
  if (!Number.isNaN(parsedTime)) {
    engine.setTemporalState(parsedTime);
  }
}
if (initialMode === 'specimen') {
  hudInstance.selectSpecimen(initialSpecimen);
}
if (initialWireframe) {
  engine.setWireframe(true);
}

// 3. Keyboard Navigation & Inspection Shortcuts
window.addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
    return;
  }
  const pois = cityDef.pois;
  const currentIdx = pois.findIndex((p) => p.id === engine.getActivePOI().id);

  switch (e.key) {
    case 'ArrowRight': {
      const next = pois[(currentIdx + 1) % pois.length];
      engine.selectPOI(next.id, true);
      soundscape.playMotifCue(next.id);
      break;
    }
    case 'ArrowLeft': {
      const prev = pois[(currentIdx - 1 + pois.length) % pois.length];
      engine.selectPOI(prev.id, true);
      soundscape.playMotifCue(prev.id);
      break;
    }
    case '1':
    case '2':
    case '3':
    case '4':
    case '5':
    case '6': {
      const idx = parseInt(e.key, 10) - 1;
      if (pois[idx]) {
        engine.selectPOI(pois[idx].id, true);
        soundscape.playMotifCue(pois[idx].id);
      }
      break;
    }
    case 'i':
    case 'I':
      hudInstance.toggleInspector();
      break;
    case 'q':
    case 'Q':
      hudInstance.toggleQrModal();
      break;
    case 'l':
    case 'L':
      hudInstance.toggleLocale();
      break;
    case 'm':
    case 'M':
      hudInstance.toggleSound();
      break;
    case 'h':
    case 'H':
      hudInstance.toggleHudVisibility();
      break;
    case 'w':
    case 'W': {
      const nextWire = !engine.getTelemetry().wireframe;
      engine.setWireframe(nextWire);
      break;
    }
    case 't':
    case 'T': {
      const nextTemp = engine.getTemporalState() < 0.5 ? 0.95 : 0.1;
      engine.setTemporalState(nextTemp);
      break;
    }
    case 'Escape':
      hudInstance.toggleQrModal(false);
      break;
  }
});

// 4. Expose programmatic inspection API on window.__ISIDORA__
declare global {
  interface Window {
    __ISIDORA__: {
      engine: InvisibleCitiesEngine;
      registry: typeof cityRegistry;
      hud: IsidoraHUD;
      selectPOI: (id: MotifId) => void;
      selectSpecimen: (id: string) => void;
      setTemporal: (val: number) => void;
      setLocale: (locale: Locale) => void;
      openQrModal: () => void;
      getTelemetry: () => EngineTelemetry;
    };
  }
}

window.__ISIDORA__ = {
  engine,
  registry: cityRegistry,
  hud: hudInstance,
  selectPOI: (id: MotifId) => engine.selectPOI(id, true),
  selectSpecimen: (id: string) => hudInstance.selectSpecimen(id),
  setTemporal: (val: number) => engine.setTemporalState(val),
  setLocale: (locale: Locale) => hudInstance.setLocale(locale),
  openQrModal: () => {
    void hudInstance.toggleQrModal(true);
  },
  getTelemetry: () => engine.getTelemetry(),
};

engine.start();
